import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { NativeAutofillMirror } from "@/components/form/native-autofill-mirror";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { US_STATES } from "@/constants/states";

/** Stored value is the two-letter code; the full name is only a search keyword. */
const STATE_OPTIONS: ComboboxOption[] = US_STATES.map((state) => ({
  value: state.code,
  label: state.code,
  keywords: [state.code],
  detail: <span className="text-sm">{state.code}</span>,
}));

/** Browser-facing labels: autofill matches the FULL state name, not the code. */
const STATE_AUTOFILL_OPTIONS = US_STATES.map((state) => ({
  value: state.code,
  label: state.name,
}));

export type StateSelectProps = FieldWrapperInjectedProps & {
  /** `""` = nothing selected. */
  value: string;
  onChange: (value: string) => void;
  /** Emitted as the hidden native select's `name` so the browser groups the address fields. */
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  modal?: boolean;
  "data-test"?: string;
};

export default function StateSelect({
  value,
  onChange,
  name = "state",
  placeholder = "Select state",
  disabled,
  ...rest
}: StateSelectProps) {
  return (
    <NativeAutofillMirror
      autoComplete="address-level1"
      name={name}
      value={value}
      onChange={onChange}
      options={STATE_AUTOFILL_OPTIONS}
      disabled={disabled}
    >
      <Combobox
        data-test="state-select-trigger"
        {...rest}
        disabled={disabled}
        value={value || null}
        onChange={(next) => onChange(next ?? "")}
        options={STATE_OPTIONS}
        placeholder={placeholder}
        searchPlaceholder="Search state..."
        emptyText="No state found."
      />
    </NativeAutofillMirror>
  );
}
